<script setup>
import { ref, computed } from 'vue';

import microsoftClient from 'dashboard/api/channel/microsoftClient';
import googleClient from 'dashboard/api/channel/googleClient';
import NextButton from 'dashboard/components-next/button/Button.vue';

import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  provider: {
    type: String,
    required: true,
    validate: value => ['microsoft', 'google'].includes(value),
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  submitButtonText: {
    type: String,
    required: true,
  },
  errorMessage: {
    type: String,
    required: true,
  },
});

const { t } = useI18n();

const isRequestingAuthorization = ref(false);

const client = computed(() => {
  if (props.provider === 'microsoft') {
    return microsoftClient;
  }

  return googleClient;
});

async function requestAuthorization() {
  try {
    isRequestingAuthorization.value = true;
    const response = await client.value.generateAuthorization();
    const {
      data: { url },
    } = response;

    window.location.href = url;
  } catch (error) {
    useAlert(props.errorMessage);
  } finally {
    isRequestingAuthorization.value = false;
  }
}
</script>

<template>
  <section
    class="w-full max-w-3xl px-4 py-6 mx-auto sm:px-6 sm:py-8"
    aria-labelledby="oauth-channel-title"
  >
    <div class="max-w-2xl">
      <p class="mb-2 text-xs font-semibold text-n-violet-11">
        {{ t('INBOX_MGMT.CREATE_FLOW.BUILDER.STEP_LABEL', { step: 2 }) }}
      </p>
      <h2
        id="oauth-channel-title"
        class="text-2xl font-semibold tracking-tight text-n-slate-12"
      >
        {{ title }}
      </h2>
      <p class="mt-2 mb-0 text-sm leading-6 text-n-slate-11">
        {{ description }}
      </p>
    </div>
    <form class="mt-7" @submit.prevent="requestAuthorization">
      <div
        class="flex justify-start min-w-0 p-5 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <NextButton
          :is-loading="isRequestingAuthorization"
          type="submit"
          solid
          blue
          :label="submitButtonText"
        />
      </div>
    </form>
  </section>
</template>
